import { DynamoDB, DynamoDBClientConfig } from "@aws-sdk/client-dynamodb";
import {
  BatchGetCommand,
  BatchGetCommandInput,
  BatchWriteCommand,
  BatchWriteCommandInput,
  DeleteCommand,
  DeleteCommandInput,
  DynamoDBDocumentClient,
  GetCommand,
  GetCommandInput,
  PutCommand,
  PutCommandInput,
  QueryCommand,
  QueryCommandInput,
  ScanCommand,
  ScanCommandInput,
  TransactGetCommand,
  TransactGetCommandInput,
  TransactWriteCommand,
  TransactWriteCommandInput,
  UpdateCommand,
  UpdateCommandInput,
} from "@aws-sdk/lib-dynamodb";
import { marshall, unmarshall } from "@aws-sdk/util-dynamodb";
import { pick } from "lodash";
import uuid from "uuid";

import {
  AnyObject,
  DynamoDBEventName,
  DynamoDBEventRecord,
  IDSK,
} from "@examspark/types";

export const eventRecordsFactory = (...records: any) => {
  return {
    Records: records,
  };
};

export const eventRecordFactory = (
  eventName: DynamoDBEventName,
  tableName: string,
  item: IDSK & AnyObject,
  params?: {
    awsRegion?: string;
    awsAccountId?: string;
    oldItem?: IDSK & AnyObject;
  },
): DynamoDBEventRecord => {
  const key = pick(item, "id", "sk");

  return {
    eventID: uuid().replace("-", ""),
    eventName,
    eventVersion: "1.1",
    eventSource: "aws:dynamodb",
    awsRegion: params?.awsRegion ?? process.env.AWS_REGION ?? "us-east-1",
    dynamodb: {
      // @todo: I think our type is wrong? Per docs this is an ISO string
      ApproximateCreationDateTime: Math.floor(Date.now() / 1000),
      Keys: marshall(key),
      NewImage:
        eventName === "REMOVE"
          ? undefined
          : marshall(item, { removeUndefinedValues: true }),
      OldImage:
        eventName === "INSERT"
          ? undefined
          : marshall(params?.oldItem ?? item, { removeUndefinedValues: true }),
      SequenceNumber: Date.now().toString(),
      // @todo: worth computing the actual size? nothing uses it
      SizeBytes: 1,
      StreamViewType: "NEW_AND_OLD_IMAGES",
    },
    eventSourceARN: generateEventSourceARN(tableName, params?.awsAccountId),
  } as DynamoDBEventRecord;
};

export const generateEventSourceARN = (
  tableName: string,
  accountId?: string,
) => {
  return [
    "arn",
    "aws",
    "dynamodb",
    "us-east-1",
    accountId ?? "000000000000",
    `table/${tableName}/stream/${Date.now().toString()}`,
  ].join(":");
};

export const getImagesFromEventRecord = (record: DynamoDBEventRecord) => {
  switch (record.eventName) {
    case "INSERT":
      return {
        newImage: unmarshall(record.dynamodb.NewImage),
        oldImage: undefined,
      };
    case "REMOVE":
      return {
        newImage: undefined,
        oldImage: unmarshall(record.dynamodb.OldImage),
      };
    case "MODIFY":
      return {
        newImage: unmarshall(record.dynamodb.NewImage),
        oldImage: unmarshall(record.dynamodb.OldImage),
      };
  }
};

export class DocumentClient extends DynamoDBDocumentClient {
  public readonly client: DynamoDB;

  constructor(clientOrConfig?: DynamoDB | DynamoDBClientConfig) {
    let client: DynamoDB;
    if (clientOrConfig instanceof DynamoDB) {
      client = clientOrConfig;
    } else if (clientOrConfig != null) {
      client = new DynamoDB(clientOrConfig);
    } else {
      client = new DynamoDB();
    }

    super(client, { marshallOptions: { removeUndefinedValues: true } });

    this.client = client;
  }

  batchGet(input: BatchGetCommandInput) {
    return this.send(new BatchGetCommand(input));
  }
  batchWrite(input: BatchWriteCommandInput) {
    return this.send(new BatchWriteCommand(input));
  }
  delete(input: DeleteCommandInput) {
    return this.send(new DeleteCommand(input));
  }
  get(input: GetCommandInput) {
    return this.send(new GetCommand(input));
  }
  put(input: PutCommandInput) {
    return this.send(new PutCommand(input));
  }
  query(input: QueryCommandInput) {
    return this.send(new QueryCommand(input));
  }
  scan(input: ScanCommandInput) {
    return this.send(new ScanCommand(input));
  }
  update(input: UpdateCommandInput) {
    return this.send(new UpdateCommand(input));
  }
  transactGet(input: TransactGetCommandInput) {
    return this.send(new TransactGetCommand(input));
  }
  transactWrite(input: TransactWriteCommandInput) {
    return this.send(new TransactWriteCommand(input));
  }
}

export type DocumentKey = NonNullable<GetCommandInput["Key"]>;
